import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useMoney } from '@/lib/hooks/useMoney'
import { cardInstallments, savingsVariant, upfrontMethodsKey, useCardQuote } from '@/lib/hooks/useCardQuote'
import { SavingsTag } from '@/components/features/checkout/SavingsTag'
import type { YearlyPriceProps } from './YearlyPrice'

export interface YearlyPriceParts {
  /** The big line: "12× de R$ X", or the year's price. */
  main: ReactNode
  /** The quiet line under it: the card total ("no cartão (total …)"), or "up to N×" / the monthly equivalent. */
  secondary: ReactNode
  /** The saving tag, or (no saving, card installments) what PIX and boleto pay up front; null when neither. */
  tag: ReactNode
}

/**
 * The lines of a yearly price, apart, for cards that line each one up with
 * its neighbours (PlanStoreCard, OfferPlanCard, CSS subgrid). `YearlyPrice`
 * stacks the same parts.
 */
type PartsInput = Pick<YearlyPriceProps, 'amount' | 'currency' | 'addOnIds' | 'saving'> & { option: YearlyPriceProps['option'] | null }

/* No yearly option (a card priced monthly): nothing, so the hook can run on every render. */
const NO_PARTS: YearlyPriceParts = { main: null, secondary: null, tag: null }

export function useYearlyPriceParts({ option, amount, currency, addOnIds, saving = null }: PartsInput): YearlyPriceParts {
  const { t } = useTranslation(['checkout', 'common'])
  const formatMoney = useMoney()
  const quote = useCardQuote(option, addOnIds)
  if (!option) return NO_PARTS
  const installments = cardInstallments(option)
  const upfrontKey = upfrontMethodsKey(option.allowedPaymentMethods)
  const money = (value: number) => formatMoney(value, currency)
  const card = quote ? t('checkout:annualPlan.installments', { count: quote.installments, amount: money(quote.each) }) : null
  const savingTag = saving && savingsVariant(option, quote) ? <SavingsTag percent={saving} option={option} quote={quote} data-testid="space-offer-saving" /> : null

  /* Top to bottom (David, 2026-10-30): the card installments, their total, then the saving (or, with no
     saving, the up-front amount); without installments, the year's price, its monthly equivalent and the saving. */
  if (card && quote) {
    return {
      main: <p className="text-3xl font-semibold tracking-tight text-gray-900">{card}</p>,
      secondary: (
        <p className="mt-0.5 text-sm text-gray-500">
          {quote.interestFree ? t('checkout:annualPlan.interestFreeOnCard') : t('checkout:annualPlan.onCardTotal', { total: money(quote.total) })}
        </p>
      ),
      /* The tag already names the up-front saving (David, 2026-10-30); without one, the line says what PIX and boleto pay. */
      tag: savingTag
        ? <div className="mt-2">{savingTag}</div>
        : upfrontKey
          ? <p className="mt-2 text-xs text-gray-500">{t('checkout:annualPlan.upfront', { methods: t(upfrontKey), amount: money(amount) })}</p>
          : null
    }
  }
  return {
    main: (
      <p className="flex flex-wrap items-baseline gap-x-1.5">
        <span className="text-3xl font-semibold tracking-tight text-gray-900">{money(amount)}</span>
        <span className="text-sm text-gray-500">{t('checkout:perYear')}</span>
      </p>
    ),
    secondary: (
      <p className="mt-0.5 text-sm text-gray-500">
        {installments > 1
          ? t('checkout:annualPlan.upTo', { count: installments })
          : t('common:storefront.monthlyEquivalent', { price: money(amount / 12) })}
      </p>
    ),
    tag: savingTag ? <div className="mt-2">{savingTag}</div> : null
  }
}

